import { Table, Card, Button, message, Spin, Input, Row, Col } from "antd";
import { useCallback, useEffect, useState } from "react";
import {
  ReloadOutlined,
  FilterOutlined,
  FileAddOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import MotionHoc from "../../../utils/MotionHoc";
import {
  HEADERS_EXPORT_BEN,
  POST_LOAN_DB2,
  POST_MAIN_REFINACE_ENFORCE,
} from "../../API/apiUrls";
import axios from "axios";
import ReBalanceFinance from "./modal/ReBalanceFinance";
import ReCheckJudgement from "./componentsReJudgment/RecheckJudgment";

const OPTIONS_CASE_TYPE = [
  { value: 1, icon: "🤝", label: "ทำยอม" },
  { value: 2, icon: "⚖️", label: "พิพากษามีดิว" },
];

const formatMoney = (v) =>
  v === null || v === undefined || v === ""
    ? "-"
    : Number(v).toLocaleString("th-TH");

const formatThaiDate = (v) =>
  v ? dayjs(v).add(543, "year").format("D MMM YY") : "-";

// สร้าง key ของแถวแบบเดียวกับ rowKey ของตาราง เพื่อไม่ให้สถานะแก้ไขชนกันเมื่อ idQueue ไม่มีค่า
const getRowKey = (r) => r.idQueue ?? r.key;

const Main = () => {
  const userId = parseInt(localStorage.getItem("USER_ID"));

  const [arrayTable, setArrayTable] = useState([]);
  const [dataArr, setDataArr] = useState([]);
  const [loading, setLoading] = useState(false);
  const [caseType, setCaseType] = useState(1);
  const [searchInputTable, setSearchInputTable] = useState("");
  const [isModal, setIsModal] = useState(false);
  const [dataRec, setDataRec] = useState(null);
  const [dataRecord, setDataRecord] = useState(null);
  const [showEditJudgment, setShowEditJudgment] = useState(false);
  const [loanDataDB2, setLoanDataDB2] = useState([]);

  useEffect(() => {
    loadMainData();
  }, []);

  // ── Load ──────────────────────────────────────────────────
  const loadMainData = async (type = caseType) => {
    if (!type) {
      message.warning("กรุณาเลือกประเภทก่อนโหลดข้อมูล");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(
        POST_MAIN_REFINACE_ENFORCE,
        { userId: 1, flagLoanNew: type },
        { headers: HEADERS_EXPORT_BEN },
      );
      if (res.status === 200) {
        if (res.data.success === "ไม่พบข้อมูล") {
          message.warning("ไม่พบข้อมูล");
          return;
        }
        const newData = (res.data?.data ?? [])
          .map((item, i) => ({
            ...item,
            key: i + 1,
          }))
          .filter(
            (item) => parseInt(item.reqRemaining) === 0 && item.idRequest,
          );

        setArrayTable(newData);
        setDataArr(newData);
      } else {
        message.error("ไม่มีข้อมูล");
        setArrayTable([]);
        setDataArr([]);
      }
    } catch (err) {
      console.error(err);
      message.error(`ไม่พบข้อมูล: ${err.message}`);
      setArrayTable([]);
      setDataArr([]);
    } finally {
      setLoading(false);
    }
  };

  // ── Load ข้อมูลสัญญา (DB2) สำหรับหน้าแก้ไขคำพิพากษา ──────
  const loadLoanData = async (contno) => {
    setLoading(true);
    const useContno = /^V\d/.test(contno) ? contno.substring(1) : contno;

    try {
      const res = await axios.post(
        POST_LOAN_DB2,
        { CONTNO: `'${useContno}'` },
        { headers: HEADERS_EXPORT_BEN },
      );
      if (res?.data?.[0]) setLoanDataDB2(res.data?.[0]);
    } catch (error) {
      if (error.name !== "CanceledError") {
        console.error("Error loadLoanData:", error);
        message.error("เกิดข้อผิดพลาดในการโหลดข้อมูลสัญญา");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenJudgment = useCallback((record) => {
    loadLoanData(record.CONTNO);
    setDataRecord(record);
    setShowEditJudgment(true);
  }, []);

  const handleCloseJudgment = useCallback(() => {
    setShowEditJudgment(false);
    setDataRecord(null);
  }, []);

  const handleReloadJudgment = useCallback(() => {
    setShowEditJudgment(false);
    setDataRecord(null);
    loadMainData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Filter ────────────────────────────────────────────────
  // ค้นหาครอบคลุมทุกฟิลด์ที่แสดงในตาราง ให้สัดส่วนของช่องค้นหาตรงกับรายละเอียดที่เพิ่มขึ้น
  const SEARCH_FIELDS = [
    "CUSCOD",
    "newContno",
    "CONTNO",
    "fullName",
    "blackCaseNumber",
    "redCaseNumber",
    "lawyerName",
  ];

  const handleFilterData = () => {
    setLoading(true);
    const q = searchInputTable.trim().toLowerCase();
    const result = (dataArr ?? []).filter(
      (item) =>
        !q ||
        SEARCH_FIELDS.map((field) =>
          String(item[field] ?? "").toLowerCase(),
        ).some((val) => val.includes(q)),
    );
    setArrayTable(result);
    setTimeout(() => setLoading(false), 250);
    result.length === 0
      ? message.error("ไม่พบข้อมูล")
      : message.success(`พบข้อมูล ${result.length} รายการ`);
  };

  if (showEditJudgment && dataRecord) {
    return (
      <ReCheckJudgement
        dataDefault={dataRecord}
        onClose={handleCloseJudgment}
        funcUpdateStatus={handleReloadJudgment}
        dataDB2={loanDataDB2}
      />
    );
  }

  // ── Columns ───────────────────────────────────────────────
  const columns = [
    {
      title: "ลำดับ",
      key: "index",
      align: "center",
      width: 60,
      render: (_, __, idx) => idx + 1,
    },
    {
      title: "เลขสัญญา",
      key: "contno",
      align: "center",
      width: 130,
      render: (_, r) => (
        <div style={{ textAlign: "center" }}>
          <div>{r.newContno ?? "-"}</div>
          {r.CONTNO && r.CONTNO !== r.newContno && (
            <div style={{ fontSize: 11, color: "#999" }}>เดิม {r.CONTNO}</div>
          )}
        </div>
      ),
    },
    {
      title: "ชื่อ-นามสกุล",
      key: "fullName",
      align: "left",
      width: 180,
      render: (_, r) => (
        <>
          <div>{r.fullName ?? "-"}</div>
          <div style={{ fontSize: 11, color: "#999" }}>{r.CUSCOD ?? "-"}</div>
        </>
      ),
    },
    {
      title: "ยอดตามคำพิพากษา",
      dataIndex: "totalJugde",
      key: "totalJugde",
      align: "right",
      width: 130,
      render: (v) => formatMoney(v),
    },
    {
      title: "ยอดหนี้คงเหลือ",
      dataIndex: "balance",
      key: "balance",
      align: "right",
      width: 120,
      render: (v) => formatMoney(v),
    },

    {
      title: "เลขคดี (ดำ/แดง)",
      key: "caseNumber",
      align: "center",
      width: 150,
      render: (_, r) => (
        <div style={{ textAlign: "center", fontSize: 12 }}>
          <div>ดำ {r.blackCaseNumber ?? "-"}</div>
          <div>แดง {r.redCaseNumber ?? "-"}</div>
          <div>วันที่พิพากษา {formatThaiDate(r.judgeDate) ?? "-"}</div>
        </div>
      ),
    },

    {
      title: "การจัดการ",
      key: "actions",
      align: "center",
      width: 170,
      render: (_, r) => {
        return (
          <Button
            type="primary"
            ghost
            icon={<FileAddOutlined />}
            onClick={() => handleOpenJudgment(r)}
            disabled={parseInt(r.reqRemaining)}
          >
            อัพเดทข้อมูล
          </Button>
        );
      },
    },
  ];

  return (
    <>
      {/* ── Case Type Chip Group ── */}
      <Card
        style={{
          marginBottom: 8,
          borderRadius: 12,
          border: "1px solid #ffcb05",
          background: "#fffdf0",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 12px",
              borderRadius: 8,
              background: "#fff3cc",
              border: "1px solid #ffcb05",
              whiteSpace: "nowrap",
            }}
          >
            <span style={{ fontSize: 14, color: "#efa420" }}>🔍</span>
            <span style={{ fontSize: 13, fontWeight: 500, color: "#efa420" }}>
              เลือกประเภท
            </span>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {OPTIONS_CASE_TYPE.map(({ value, icon, label }) => {
              const isActive = caseType === value;
              return (
                <button
                  key={value}
                  onClick={() => {
                    setCaseType(value);
                    loadMainData(value);
                  }}
                  disabled={loading}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    padding: "6px 14px",
                    borderRadius: 20,
                    border: `1.5px solid ${isActive ? "#efa420" : "#d9d9d9"}`,
                    background: isActive
                      ? "linear-gradient(135deg, #ffcb05, #efa420)"
                      : "#fff",
                    color: isActive ? "#fff" : "#555",
                    fontWeight: isActive ? 600 : 400,
                    fontSize: 13,
                    cursor: loading ? "not-allowed" : "pointer",
                    opacity: loading ? 0.6 : 1,
                    boxShadow: isActive
                      ? "0 2px 8px rgba(239,164,32,0.45)"
                      : "none",
                    transition: "all 0.2s",
                    whiteSpace: "nowrap",
                  }}
                >
                  <span>{icon}</span>
                  <span>{label}</span>
                  {isActive && dataArr.length > 0 && (
                    <span
                      style={{
                        marginLeft: 4,
                        background: "rgba(255,255,255,0.35)",
                        borderRadius: 10,
                        padding: "0 6px",
                        fontSize: 11,
                        fontWeight: 700,
                        color: "#fff",
                      }}
                    >
                      {dataArr.length}
                    </span>
                  )}
                </button>
              );
            })}
            <Button
              icon={<ReloadOutlined />}
              type="primary"
              onClick={() => loadMainData()}
              loading={loading}
              disabled={!caseType}
              style={{
                borderRadius: 20,
                height: 34,
                paddingInline: 16,
                fontWeight: 600,
                fontSize: 13,
                background: "linear-gradient(135deg, #ffcb05, #efa420)",
                border: "none",
                boxShadow: "0 2px 8px rgba(239,164,32,0.45)",
              }}
            >
              โหลดข้อมูล
            </Button>
          </div>

          {loading && (
            <span
              style={{
                marginLeft: "auto",
                color: "#efa420",
                fontSize: 12,
                whiteSpace: "nowrap",
              }}
            >
              ⏳ กำลังโหลด...
            </span>
          )}
        </div>
      </Card>

      <Card style={{ marginBottom: 12 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={12} md={9}>
            <Input
              placeholder="ค้นหาสัญญา / ชื่อ / เลขบัตร / เลขคดี / ทนาย"
              value={searchInputTable}
              onChange={(e) => setSearchInputTable(e.target.value)}
              onPressEnter={handleFilterData}
              style={{ borderRadius: 8, width: "100%" }}
              allowClear
              size="small"
            />
          </Col>
          <Col xs={12} sm={12} md={4}>
            <Button
              icon={<FilterOutlined />}
              onClick={handleFilterData}
              disabled={dataArr.length === 0}
              block
              style={{
                borderRadius: 8,
                backgroundColor: "#fff7d6",
                border: "1px solid #ffe58f",
              }}
            >
              กรองข้อมูล
            </Button>
          </Col>
          <Col xs={12} sm={12} md={4}>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                setSearchInputTable("");
                setArrayTable(dataArr);
              }}
              disabled={dataArr.length === 0}
              block
            >
              ล้าง
            </Button>
          </Col>
        </Row>
      </Card>

      <Card>
        <Spin spinning={loading} size="large" tip="Loading...">
          <Table
            size="small"
            columns={columns}
            dataSource={arrayTable}
            rowKey={getRowKey}
            scroll={{ x: "max-content" }}
            pagination={{
              defaultPageSize: 20,
              showSizeChanger: true,
              showTotal: (t) => `ทั้งหมด ${t} รายการ`,
            }}
            footer={() => (
              <span>จำนวนสัญญาทั้งหมด {arrayTable.length} รายการ</span>
            )}
          />
        </Spin>
        {isModal && (
          <ReBalanceFinance
            open={isModal}
            close={setIsModal}
            dataDefault={dataRec}
            onSuccess={loadMainData}
          />
        )}
      </Card>
    </>
  );
};

const MainRejudgementEnforce = MotionHoc(Main);
export default MainRejudgementEnforce;
